import { useState, type ReactElement } from 'react';
import { Box, Button, Group, TextInput } from '@mantine/core';
import { Sparkles } from 'lucide-react';

interface AIExplorerComposePromptProps {
    onCompose: (prompt: string) => void;
    composing: boolean;
    /** Optional one-tap example prompts. */
    examples?: string[];
}

/**
 * The dashboard generation control: a prompt the user describes, which an LLM
 * (in the BFF) turns into a layout choice and a stream of tile configs. This is
 * the entry point for the dynamic-layout feature — distinct from the page's
 * standard AMA bar (which answers ad-hoc questions).
 */
export default function AIExplorerComposePrompt({ onCompose, composing, examples = [] }: AIExplorerComposePromptProps): ReactElement {
    const [value, setValue] = useState('');
    const submit = (): void => {
        if (value.trim().length > 0) onCompose(value);
    };

    return (
        <Box mb="lg">
            <Group gap="sm" wrap="nowrap" align="flex-end">
                <TextInput
                    style={{ flex: 1 }}
                    size="md"
                    label="Compose a dashboard"
                    placeholder="e.g. Show adoption across teams over the last quarter"
                    leftSection={<Sparkles size={16} />}
                    value={value}
                    onChange={(event) => setValue(event.currentTarget.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') submit();
                    }}
                    disabled={composing}
                />
                <Button size="md" onClick={submit} loading={composing} disabled={value.trim().length === 0}>
                    Compose
                </Button>
            </Group>
            {examples.length > 0 && (
                <Group gap="xs" mt="xs">
                    {examples.map((example) => (
                        <Button
                            key={example}
                            variant="light"
                            size="compact-xs"
                            radius="xl"
                            disabled={composing}
                            onClick={() => {
                                setValue(example);
                                onCompose(example);
                            }}
                        >
                            {example}
                        </Button>
                    ))}
                </Group>
            )}
        </Box>
    );
}
